import {
  calculateWizardState,
  getProgressForRoute,
  pageConfigById,
  getWizardNavigationState,
  getWizardRoutes,
  getWizardSectionRoutes,
  IndexedPage,
  indexedPageById,
  selectableCategoriesInGroups,
  WizardNavigationState,
  WizardRoute
} from '@features/formation-viewer/formation-wizard/model'
import type { STFolderReadyState } from './st-folder-types'
import type {
  FolderQuestionnaireState,
  QuestionnaireMfaGateState
} from './st-folder-questionnaire-types'
import type { FolderMembershipSide } from './folder-membership-side'
import {
  IncompleteReasonOption,
  PersonalizedNote,
  STChecklistItem,
  STDocument,
  STOpenFolderState,
  SDKResponseFor,
  SubmitClientOrganizerValidationResult
} from '@st/sdk'
import {
  categorySelectInput,
  QuestionnaireCategoryOption,
  WizardCategoryConfig,
  WizardConfig,
  WizardPageConfig
} from '@st/ui-config'
import { Update, type UpdateResult } from '@st/redux'
import type { JsonMap } from '@st/util/json-value'
import { Progress } from '@st/util/progress'
import { Effect } from 'effect'
import { match, P } from '@st/pattern'
import {
  formatValidationErrors,
  validateQuestionnairePage
} from '../st-questionnaire/st-questionnaire-validation'
import type { QuestionnairePageValidationResult } from '../st-questionnaire/st-questionnaire-validation'
import {
  getClientMfaIdentifiers,
  matchesClientMfaLast4
} from '../st-questionnaire/client-mfa-gate-logic'
import { folderMembershipSide } from './folder-membership-side'
import { selResolvedInputs } from './st-folder-state-selectors'

/**
 * Elm composition:
 * - `stFolderModule` owns the long-lived folder state.
 * - This file is a composed helper/update for questionnaire UI state.
 * - It returns pure state + command intents; parent interprets commands and runs effects.
 */
export type {
  FolderQuestionnaireDashboardState,
  FolderQuestionnaireSubmitDialogState,
  FolderQuestionnaireState,
  QuestionnaireMfaGateState
} from './st-folder-questionnaire-types'

type ReadyQuestionnaireState = Extract<FolderQuestionnaireState, { status: 'ready' }>

export type FolderQuestionnaireMessage =
  | { type: 'enterQuestionnaire' }
  | { type: 'navigateNext' }
  | { type: 'navigatePrev' }
  | { type: 'navigate'; route: WizardRoute; autoSelectCategoryIfMissing?: boolean }
  | { type: 'returnToDashboard' }
  | { type: 'rerunValidation' }
  | { type: 'deleteDocument'; docId: string }
  | { type: 'markQuestionnaireAsCompleted' }
  | { type: 'openSubmitDialog' }
  | {
      type: 'submitClientOrganizer'
      forceSubmit: boolean
      incompleteReasonOptions: IncompleteReasonOption[]
    }
  | { type: 'submitRequestFailed' }
  | { type: 'closeSubmitDialog' }
  | {
      type: 'submitValidationReceived'
      validationResult: SubmitClientOrganizerValidationResult
    }
  | { type: 'submitValidationFailed' }
  | { type: 'submitCompleted'; response: SDKResponseFor<'folders/submitClientOrganizer'> }
  | { type: 'mfaLast4Changed'; value: string }
  | { type: 'mfaContinueClicked' }
  | { type: 'mfaGrantStatusLoaded'; hasGrant: boolean }
  | { type: 'autosaveFired'; pageId: string; progress: number }

export type FolderQuestionnaireCommand =
  | { type: 'setFolderInput'; inputKey: string; value: boolean }
  | { type: 'deleteDocument'; folderId: string; docId: string }
  | { type: 'markQuestionnaireAsCompleted'; folderId: string }
  | {
      type: 'saveIncompleteReasonsBeforeSubmit'
      folderId: string
      forceSubmit: boolean
      incompleteReasonOptions: IncompleteReasonOption[]
    }
  | { type: 'submitClientOrganizer'; folderId: string; forceSubmit: boolean }
  | { type: 'saveQuestionnaireProgressNow'; folderId: string; pageId: string; progress: number }
  | {
      type: 'saveQuestionnaireProgressAutosave'
      folderId: string
      pageId: string
      progress: number
    }
  | { type: 'writeMfaGrantCookie'; folderId: string }

export type QuestionnaireCommandMessage = {
  type: 'questionnaireCommand'
  command: FolderQuestionnaireCommand
}
export type FolderQuestionnaireUpdate = UpdateResult<
  FolderQuestionnaireState,
  QuestionnaireCommandMessage
>
type ReadyQuestionnaireUpdate = UpdateResult<ReadyQuestionnaireState, QuestionnaireCommandMessage>

type QuestionnaireReduceContext = {
  stFolder: STFolderReadyState
  folderState: STOpenFolderState
  membershipSide: FolderMembershipSide
  inputs: JsonMap
  isPreview: boolean
}

export function initFolderQuestionnaireState(): FolderQuestionnaireState {
  return { status: 'idle' }
}

function isQuestionnaireViewIntent(message: FolderQuestionnaireMessage): boolean {
  return match(message)
    .with(
      {
        type: P.union(
          'enterQuestionnaire',
          'navigateNext',
          'navigatePrev',
          'navigate',
          'returnToDashboard',
          'rerunValidation',
          'deleteDocument',
          'markQuestionnaireAsCompleted',
          'openSubmitDialog',
          'submitClientOrganizer',
          'closeSubmitDialog',
          'mfaLast4Changed',
          'mfaContinueClicked'
        )
      },
      () => true
    )
    .otherwise(() => false)
}

function isIgnoredQuestionnaireViewIntent({
  state,
  message
}: Readonly<{ state: FolderQuestionnaireState; message: FolderQuestionnaireMessage }>): boolean {
  if (!isQuestionnaireViewIntent(message)) return false
  if (state.status == 'idle') return true

  return match(message)
    .with({ type: 'enterQuestionnaire' }, () => state.selectedMode == 'questionnaire')
    .with(
      { type: P.union('navigateNext', 'navigatePrev', 'navigate', 'rerunValidation') },
      () => state.selectedMode != 'questionnaire'
    )
    .with({ type: 'returnToDashboard' }, () => state.selectedMode == 'dashboard')
    .with({ type: 'openSubmitDialog' }, () => state.dashboard.status != 'idle')
    .with({ type: 'submitClientOrganizer' }, () => state.dashboard.status != 'open')
    .with(
      { type: 'closeSubmitDialog' },
      () => state.dashboard.status == 'idle' || state.dashboard.status == 'finishing'
    )
    .with(
      { type: P.union('mfaLast4Changed', 'mfaContinueClicked') },
      () => state.mfaGate.status != 'locked'
    )
    .otherwise(() => false)
}

function unexpectedQuestionnaireMessage(
  state: FolderQuestionnaireState,
  message: FolderQuestionnaireMessage
) {
  return new Error(`Unexpected questionnaire message ${message.type} while ${state.status}`)
}

function chainUpdate<State, Message extends { type: string }>(
  update: UpdateResult<State, Message>,
  next: (state: State) => UpdateResult<State, Message>
): UpdateResult<State, Message> {
  return Update.pipe<State, Message>(update, next)
}

export function reduceFolderQuestionnaire(args: {
  state: FolderQuestionnaireState
  message: FolderQuestionnaireMessage
  stFolder: STFolderReadyState
}): FolderQuestionnaireUpdate {
  const { state, message, stFolder } = args
  if (isIgnoredQuestionnaireViewIntent({ state, message })) {
    return state
  }

  if (state.status != 'ready') {
    throw unexpectedQuestionnaireMessage(state, message)
  }
  const ctx = buildQuestionnaireReduceContext(stFolder)
  const update = reduceFolderQuestionnaireReadyOnly({ state, message, ctx })
  return chainUpdate(update, (nextState) => reconcileMfaGateState({ state: nextState, ctx }))
}

export function reconcileFolderQuestionnaire(args: {
  state: FolderQuestionnaireState
  stFolder: STFolderReadyState
}): FolderQuestionnaireUpdate {
  const { state, stFolder } = args
  const ctx = buildQuestionnaireReduceContext(stFolder)
  if (state.status == 'ready') {
    return reconcileMfaGateState({ state, ctx })
  }

  const formConfig = stFolder.formationConfig
  if (formConfig.tag != 'loaded' || formConfig.schemaId != ctx.folderState.schemaId) {
    return state
  }
  const wizardConfig = formConfig.config.wizard
  if (!wizardConfig) {
    return state
  }

  const route = selInitialQuestionnaireRoute({
    lastSavedPageId: ctx.folderState.folder.clientOrganizer.questionnaireLastPageId,
    inputs: ctx.inputs,
    priorInputs: ctx.folderState.prevInputs ?? {},
    membershipSide: ctx.membershipSide,
    wizardConfig,
    settings: ctx.folderState.settings,
    hiddenCategoryIds: ctx.folderState.hiddenCategoryIds ?? [],
    hiddenPageIds: ctx.folderState.hiddenPageIds ?? []
  })
  const next: ReadyQuestionnaireState = {
    status: 'ready',
    selectedMode: 'dashboard',
    mfaGate: { status: 'checking' },
    dashboard: { status: 'idle' },
    questionnaire: { route, showValidationErrors: false }
  }

  return reconcileMfaGateState({ state: next, ctx })
}

function reduceFolderQuestionnaireReadyOnly(args: {
  state: ReadyQuestionnaireState
  message: FolderQuestionnaireMessage
  ctx: QuestionnaireReduceContext
}): ReadyQuestionnaireUpdate {
  const { state, message, ctx } = args
  const folderId = ctx.folderState.folder.id
  const wizardConfig = selQuestionnaireWizardConfig(ctx.stFolder)

  switch (message.type) {
    case 'enterQuestionnaire':
      return { ...state, selectedMode: 'questionnaire' }

    case 'navigatePrev': {
      const navState = selQuestionnaireNavState(state, { stFolder: ctx.stFolder })
      if (!navState?.prev) return state
      const route = navState.prev
      return { ...state, questionnaire: { route, showValidationErrors: false } }
    }

    case 'navigateNext': {
      const navState = selQuestionnaireNavState(state, { stFolder: ctx.stFolder })
      const curPage = selQuestionnaireCurrentPage(state, { stFolder: ctx.stFolder })
      const validationResult = validateQuestionnairePage(
        selQuestionnaireCategoryOptions(wizardConfig),
        curPage?.config.components ?? [],
        ctx.inputs
      )

      if (!validationResult.ok) {
        return {
          ...state,
          questionnaire: { ...state.questionnaire, showValidationErrors: true }
        }
      }

      if (!navState?.next) {
        return state
      }
      const route = navState.next
      return { ...state, questionnaire: { route, showValidationErrors: false } }
    }

    case 'navigate':
      return withAutoSelectCategoryInputCommand({
        state: {
          ...state,
          questionnaire: { route: message.route, showValidationErrors: false }
        },
        ctx,
        route: message.route,
        autoSelectCategoryIfMissing: message.autoSelectCategoryIfMissing
      })

    case 'returnToDashboard': {
      const saveCommand = maybeSaveProgressNowCommand({ state, ctx })
      const next: ReadyQuestionnaireState = {
        ...state,
        selectedMode: 'dashboard',
        dashboard: { status: 'idle' }
      }
      if (!saveCommand) {
        return next
      }
      return [next, Effect.succeed({ type: 'questionnaireCommand', command: saveCommand })] as const
    }

    case 'rerunValidation': {
      const curPage = selQuestionnaireCurrentPage(state, { stFolder: ctx.stFolder })
      const validationResult = validateQuestionnairePage(
        selQuestionnaireCategoryOptions(wizardConfig),
        curPage?.config.components ?? [],
        ctx.inputs
      )
      if (validationResult.ok) {
        return {
          ...state,
          questionnaire: { ...state.questionnaire, showValidationErrors: false }
        }
      }
      return {
        ...state,
        questionnaire: { ...state.questionnaire, showValidationErrors: true }
      }
    }

    case 'openSubmitDialog':
      return { ...state, dashboard: { status: 'validating' } }

    case 'submitValidationReceived': {
      if (state.dashboard.status != 'validating') {
        throw unexpectedQuestionnaireMessage(state, message)
      }

      return {
        ...state,
        dashboard: {
          status: 'open',
          validationResult: message.validationResult,
          submissionCount: 0
        }
      }
    }

    case 'submitValidationFailed':
      if (state.dashboard.status != 'validating') {
        throw unexpectedQuestionnaireMessage(state, message)
      }
      return { ...state, dashboard: { status: 'idle' } }

    case 'submitClientOrganizer': {
      if (state.dashboard.status != 'open') {
        throw unexpectedQuestionnaireMessage(state, message)
      }

      const next: ReadyQuestionnaireState = {
        ...state,
        dashboard: {
          status: 'submitting',
          validationResult: state.dashboard.validationResult,
          submissionCount: state.dashboard.submissionCount + 1
        }
      }

      return [
        next,
        Effect.succeed({
          type: 'questionnaireCommand',
          command: {
            type: 'saveIncompleteReasonsBeforeSubmit',
            folderId,
            forceSubmit: message.forceSubmit,
            incompleteReasonOptions: message.incompleteReasonOptions
          }
        })
      ] as const
    }

    case 'submitCompleted': {
      if (state.dashboard.status == 'finishing') {
        return { ...state, dashboard: { status: 'idle' } }
      }
      if (state.dashboard.status != 'submitting') {
        throw unexpectedQuestionnaireMessage(state, message)
      }
      if (message.response.validationResult?.status == 'missing_reasons_error') {
        return {
          ...state,
          dashboard: {
            status: 'open',
            validationResult: message.response.validationResult,
            submissionCount: state.dashboard.submissionCount
          }
        }
      }
      return { ...state, dashboard: { status: 'idle' } }
    }

    case 'submitRequestFailed': {
      if (state.dashboard.status == 'finishing') {
        return { ...state, dashboard: { status: 'idle' } }
      }
      if (state.dashboard.status != 'submitting') {
        throw unexpectedQuestionnaireMessage(state, message)
      }

      return {
        ...state,
        dashboard: {
          status: 'open',
          validationResult: state.dashboard.validationResult,
          submissionCount: state.dashboard.submissionCount
        }
      }
    }

    case 'mfaLast4Changed':
      if (state.mfaGate.status != 'locked') {
        return state
      }
      return {
        ...state,
        mfaGate: {
          ...state.mfaGate,
          last4: message.value.replace(/\D/g, '').slice(0, 4),
          attempted: false
        }
      }

    case 'mfaContinueClicked': {
      if (state.mfaGate.status != 'locked' || state.mfaGate.last4.length != 4) {
        return state
      }
      const identifiers = getClientMfaIdentifiers(ctx.folderState)
      const matches = matchesClientMfaLast4(identifiers, state.mfaGate.last4)
      if (!matches) {
        return {
          ...state,
          mfaGate: {
            ...state.mfaGate,
            attempted: true
          }
        }
      }

      return [
        { ...state, mfaGate: { status: 'unlocked' } },
        Effect.succeed({
          type: 'questionnaireCommand',
          command: { type: 'writeMfaGrantCookie', folderId }
        })
      ] as const
    }

    case 'mfaGrantStatusLoaded': {
      if (state.mfaGate.status != 'checking') {
        throw unexpectedQuestionnaireMessage(state, message)
      }
      if (message.hasGrant) {
        return { ...state, mfaGate: { status: 'unlocked' } }
      }
      return {
        ...state,
        mfaGate: {
          status: 'locked',
          last4: '',
          attempted: false
        }
      }
    }

    case 'closeSubmitDialog':
      if (state.dashboard.status == 'submitting') {
        return { ...state, dashboard: { status: 'finishing' } }
      }
      return { ...state, dashboard: { status: 'idle' } }

    case 'deleteDocument':
      return [
        state,
        Effect.succeed({
          type: 'questionnaireCommand',
          command: {
            type: 'deleteDocument',
            folderId,
            docId: message.docId
          }
        })
      ] as const

    case 'markQuestionnaireAsCompleted': {
      const next: ReadyQuestionnaireState = {
        ...state,
        selectedMode: 'dashboard',
        dashboard: { status: 'idle' }
      }

      // Completion must be immediate. Do not send saveQuestionnaireProgress first:
      // markQuestionnaireAsCompleted is the authoritative write that sets progress to 1.0.
      return [
        next,
        Effect.succeed({
          type: 'questionnaireCommand',
          command: {
            type: 'markQuestionnaireAsCompleted',
            folderId
          }
        })
      ] as const
    }

    case 'autosaveFired': {
      return [
        state,
        Effect.succeed({
          type: 'questionnaireCommand',
          command: {
            type: 'saveQuestionnaireProgressAutosave',
            folderId,
            pageId: message.pageId,
            progress: message.progress
          }
        })
      ] as const
    }

    default: {
      message satisfies never
      throw unexpectedQuestionnaireMessage(state, message)
    }
  }
}

function reconcileMfaGateState(args: {
  state: ReadyQuestionnaireState
  ctx: QuestionnaireReduceContext
}): ReadyQuestionnaireUpdate {
  const { state, ctx } = args
  if (!isMfaGateRequired(ctx)) {
    if (state.mfaGate.status == 'disabled') {
      return state
    }
    return {
      ...state,
      mfaGate: { status: 'disabled' }
    }
  }

  if (state.mfaGate.status != 'disabled') {
    return state
  }

  return { ...state, mfaGate: { status: 'checking' } }
}

function isMfaGateRequired(ctx: QuestionnaireReduceContext) {
  if (ctx.isPreview) return false
  if (ctx.membershipSide != 'client') return false
  if (ctx.folderState.clientOrganizerMfaEnabled != true) return false
  return getClientMfaIdentifiers(ctx.folderState).length > 0
}

function withAutoSelectCategoryInputCommand(args: {
  state: ReadyQuestionnaireState
  ctx: QuestionnaireReduceContext
  route: WizardRoute
  autoSelectCategoryIfMissing: boolean | undefined
}): ReadyQuestionnaireUpdate {
  const { state, ctx, route, autoSelectCategoryIfMissing } = args
  if (!autoSelectCategoryIfMissing) {
    return state
  }

  const inputKey = route.categoryId ? categorySelectInput(route.categoryId) : undefined
  if (!inputKey || ctx.folderState.inputs[inputKey]) {
    return state
  }

  return [
    state,
    Effect.succeed({
      type: 'questionnaireCommand',
      command: { type: 'setFolderInput', inputKey, value: true }
    })
  ] as const
}

export function selQuestionnaireAutosave(
  state: FolderQuestionnaireState,
  ctx: { stFolder: STFolderReadyState }
): { pageId: string; progress: number } | undefined {
  if (state.status != 'ready') return undefined

  const reduceContext = buildQuestionnaireReduceContext(ctx.stFolder)
  const { stFolder } = ctx
  if (reduceContext.isPreview) return undefined

  if (reduceContext.membershipSide != 'client') return undefined
  if (state.selectedMode != 'questionnaire') return undefined

  const pageId = state.questionnaire.route.pageId
  const progress = Progress.toNumber(selQuestionnaireProgress(state, { stFolder }))

  const persistedPageId = reduceContext.folderState.folder.clientOrganizer.questionnaireLastPageId
  const persistedProgress = reduceContext.folderState.folder.clientOrganizer.questionnaireProgress
  if (persistedPageId == pageId && persistedProgress == progress) {
    return undefined
  }

  return { pageId, progress }
}

function maybeSaveProgressNowCommand(args: {
  state: ReadyQuestionnaireState
  ctx: QuestionnaireReduceContext
}): FolderQuestionnaireCommand | undefined {
  const { state, ctx } = args
  if (ctx.isPreview) return undefined
  if (ctx.membershipSide != 'client') return undefined

  const pageId = state.questionnaire.route.pageId
  const progress = Progress.toNumber(selQuestionnaireProgress(state, { stFolder: ctx.stFolder }))

  return {
    type: 'saveQuestionnaireProgressNow',
    folderId: ctx.folderState.folderId,
    pageId,
    progress
  }
}

function buildQuestionnaireReduceContext(stFolder: STFolderReadyState): QuestionnaireReduceContext {
  return {
    stFolder,
    folderState: stFolder.folderState,
    membershipSide: selQuestionnaireMembershipSide(stFolder),
    inputs: selResolvedInputs(stFolder),
    isPreview: isQuestionnairePreview(stFolder)
  }
}

function selQuestionnaireMembershipSide(stFolder: STFolderReadyState): FolderMembershipSide {
  if (!stFolder.currentUserId) return 'client'
  return folderMembershipSide(stFolder.folderState.memberships, stFolder.currentUserId) ?? 'client'
}

export function selQuestionnaireValidationErrorMessage(
  state: FolderQuestionnaireState,
  ctx: { stFolder: STFolderReadyState }
) {
  if (state.status != 'ready') {
    return undefined
  }
  const validationResult = selQuestionnaireValidationResult(state, ctx)
  if (validationResult.ok) {
    return undefined
  }
  return formatValidationErrors(validationResult.errors)
}

export function selQuestionnaireValidationResult(
  state: Extract<FolderQuestionnaireState, { status: 'ready' }>,
  ctx: { stFolder: STFolderReadyState }
): QuestionnairePageValidationResult {
  if (!state.questionnaire.showValidationErrors) {
    return { ok: true }
  }
  const wizardConfig = selQuestionnaireWizardConfig(ctx.stFolder)
  const curPage = selQuestionnaireCurrentPage(state, ctx)
  return validateQuestionnairePage(
    selQuestionnaireCategoryOptions(wizardConfig),
    curPage?.config.components ?? [],
    selResolvedInputs(ctx.stFolder)
  )
}

export function hasValidationError(
  validationResult: QuestionnairePageValidationResult,
  inputKey: string
) {
  if (validationResult.ok) return false
  return validationResult.errors.some((e) => e.keys.includes(inputKey))
}

type QuestionnairePage = {
  config: WizardPageConfig
  personalizedNote: PersonalizedNote | undefined
  organizationPreviewNote: string | undefined
}

export function selQuestionnaireCurrentPage(
  state: Extract<FolderQuestionnaireState, { status: 'ready' }>,
  ctx: { stFolder: STFolderReadyState }
): QuestionnairePage | undefined {
  const folderState = ctx.stFolder.folderState
  const inputs = selResolvedInputs(ctx.stFolder)
  const membershipSide = selQuestionnaireMembershipSide(ctx.stFolder)
  const wizardState = selQuestionnaireState({
    inputs,
    priorInputs: folderState.prevInputs ?? {},
    membershipSide,
    wizardConfig: selQuestionnaireWizardConfig(ctx.stFolder),
    settings: folderState.settings,
    hiddenCategoryIds: folderState.hiddenCategoryIds ?? [],
    hiddenPageIds: folderState.hiddenPageIds ?? []
  })

  const config = pageConfigById(wizardState, state.questionnaire.route.pageId)
  if (!config) return undefined

  return {
    config,
    personalizedNote: undefined,
    organizationPreviewNote: isQuestionnairePreview(ctx.stFolder)
      ? generateOrganizationPreviewNote(config)
      : undefined
  }
}

function isQuestionnairePreview(stFolder: STFolderReadyState): boolean {
  return stFolder.folderViewMode == 'organization_preview_questionnaire'
}

function generateOrganizationPreviewNote(config: WizardPageConfig) {
  if (config.components.some((c) => c.__typename == 'CategorySelectorConfig')) {
    return 'This is a preview. You can add or remove the categories below in Settings.'
  }
  return 'This is a preview. You can customize this questionnaire or turn it off entirely in Settings.'
}

export function selQuestionnaireNavState(
  state: Extract<FolderQuestionnaireState, { status: 'ready' }>,
  ctx: { stFolder: STFolderReadyState }
) {
  const folderState = ctx.stFolder.folderState

  const inputs = selResolvedInputs(ctx.stFolder)
  const membershipSide = selQuestionnaireMembershipSide(ctx.stFolder)
  const wizardState = selQuestionnaireState({
    inputs,
    priorInputs: folderState.prevInputs ?? {},
    membershipSide,
    wizardConfig: selQuestionnaireWizardConfig(ctx.stFolder),
    settings: folderState.settings,
    hiddenCategoryIds: folderState.hiddenCategoryIds ?? [],
    hiddenPageIds: folderState.hiddenPageIds ?? []
  })
  const routes = getWizardRoutes(wizardState)
  return getWizardNavigationState(routes, state.questionnaire.route)
}

export function selQuestionnaireProgress(
  state: Extract<FolderQuestionnaireState, { status: 'ready' }>,
  ctx: { stFolder: STFolderReadyState }
): Progress {
  const folderState = ctx.stFolder.folderState

  const inputs = selResolvedInputs(ctx.stFolder)
  const membershipSide = selQuestionnaireMembershipSide(ctx.stFolder)
  const wizardState = selQuestionnaireState({
    inputs,
    priorInputs: folderState.prevInputs ?? {},
    membershipSide,
    wizardConfig: selQuestionnaireWizardConfig(ctx.stFolder),
    settings: folderState.settings,
    hiddenCategoryIds: folderState.hiddenCategoryIds ?? [],
    hiddenPageIds: folderState.hiddenPageIds ?? []
  })
  return getProgressForRoute(wizardState, state.questionnaire.route.pageId)
}

export type WizardSectionTab = { label: string; route: WizardRoute }
export function selQuestionnaireTabs(
  state: Extract<FolderQuestionnaireState, { status: 'ready' }>,
  ctx: { stFolder: STFolderReadyState }
): WizardSectionTab[] {
  const folderState = ctx.stFolder.folderState

  const inputs = selResolvedInputs(ctx.stFolder)
  const priorInputs = folderState.prevInputs ?? {}
  const membershipSide = selQuestionnaireMembershipSide(ctx.stFolder)

  const wizardState = selQuestionnaireState({
    inputs,
    priorInputs,
    membershipSide,
    wizardConfig: selQuestionnaireWizardConfig(ctx.stFolder),
    settings: folderState.settings,
    hiddenCategoryIds: folderState.hiddenCategoryIds ?? [],
    hiddenPageIds: folderState.hiddenPageIds ?? []
  })

  const routes = getWizardRoutes(wizardState)
  return getWizardSectionRoutes(routes).map((route) => {
    const section = selQuestionnaireWizardConfig(ctx.stFolder).sections.find(
      (s) => s.id == route.sectionId
    )
    return { label: section ? section.name : '<missing>', route }
  })
}

export function selQuestionnairePersonalizedNote(
  state: Extract<FolderQuestionnaireState, { status: 'ready' }>,
  ctx: { stFolder: STFolderReadyState }
) {
  const folderState = ctx.stFolder.folderState

  const pageId = state.questionnaire.route?.pageId
  if (!pageId) return undefined
  return folderState.personalizedNotes.find((n) => n.pageId == pageId)
}

export function categorySelectorCategoryGroups(
  stFolder: STFolderReadyState,
  sectionId: string,
  hiddenCategoryIds: string[]
) {
  const allSections = selectableCategoriesInGroups(
    getCategoriesForSection(selQuestionnaireWizardConfig(stFolder), sectionId)
  )
  if (hiddenCategoryIds.length == 0) return allSections

  return allSections
    .filter((s) => s.items.some((c) => !hiddenCategoryIds.includes(c.id)))
    .map((s) => ({ ...s, items: s.items.filter((c) => !hiddenCategoryIds.includes(c.id)) }))
}

function selQuestionnaireWizardConfig(stFolder: STFolderReadyState): WizardConfig {
  const formationConfig = stFolder.formationConfig
  if (
    formationConfig.tag == 'loaded' &&
    formationConfig.schemaId == stFolder.folderState.schemaId &&
    formationConfig.config.wizard
  ) {
    return formationConfig.config.wizard
  }
  throw new Error(`Missing wizard config for ${stFolder.folderState.schemaId}`)
}

function selInitialQuestionnaireRoute(arg: {
  inputs: JsonMap
  priorInputs: JsonMap
  membershipSide: FolderMembershipSide
  wizardConfig: WizardConfig
  lastSavedPageId: string | undefined
  settings: JsonMap
  hiddenCategoryIds: string[]
  hiddenPageIds: string[]
}): WizardRoute {
  const wizardState = selQuestionnaireState({
    inputs: arg.inputs,
    priorInputs: arg.priorInputs,
    membershipSide: arg.membershipSide,
    wizardConfig: arg.wizardConfig,
    settings: arg.settings,
    hiddenCategoryIds: arg.hiddenCategoryIds,
    hiddenPageIds: arg.hiddenPageIds
  })

  const lastSavedPage = arg.lastSavedPageId
    ? indexedPageById(wizardState, arg.lastSavedPageId)
    : undefined

  if (lastSavedPage) {
    return routeForIndexedPage(lastSavedPage)
  }

  return routeForIndexedPage(wizardState.filteredPages[0])
}

function routeForIndexedPage(indexedPage: IndexedPage) {
  return {
    pageId: indexedPage.page.id,
    sectionId: indexedPage.section.id,
    categoryId: indexedPage.category?.id
  }
}

function selQuestionnaireState(arg: {
  inputs: JsonMap
  priorInputs: JsonMap
  wizardConfig: WizardConfig
  membershipSide: FolderMembershipSide
  settings: JsonMap
  hiddenCategoryIds: string[]
  hiddenPageIds: string[]
}) {
  const context = { role: arg.membershipSide }
  return calculateWizardState({
    inputs: arg.inputs,
    priorInputs: arg.priorInputs,
    uiConfig: arg.wizardConfig,
    context,
    settings: arg.settings,
    hiddenCategoryIds: arg.hiddenCategoryIds,
    hiddenPageIds: arg.hiddenPageIds
  })
}

export type DocumentStackItem = {
  doc: STDocument
  checklistItemCandidates: STChecklistItem[]
}

export function selQuestionnaireCategoryOptions(
  uiConfig: WizardConfig
): QuestionnaireCategoryOption[] {
  const categoryOptions: QuestionnaireCategoryOption[] = []
  for (const section of uiConfig.sections) {
    for (const category of section.categories) {
      categoryOptions.push({
        sectionId: section.id,
        label: category.name,
        selectedUserInputKey: category.isSelectedUserInputKey!
      })
    }
  }
  return categoryOptions
}

function getCategoriesForSection(
  uiConfig: WizardConfig,
  sectionId: string
): WizardCategoryConfig[] {
  const section = uiConfig.sections.find((el) => el.id == sectionId)
  if (section == null) return []
  return section.categories
}

export type { WizardNavigationState }
